/**
 * Cross-step draft storage for the CodeLive create wizard.
 *
 * Step 1 writes the draft + flow flag and seeds planner topics.
 * Steps 2–4 read the draft; RequireCreateDraft guards missing drafts.
 * Step 4 clears everything after a successful API create.
 */

import {
  getWorkshopTopicDurationMinutes,
  type WorkshopDayTiming,
} from './workshopSessionUtils';
import type { SessionCategory } from './scheduledSessionStorage';
import type { TimelineTopicItem } from '../types/topicQuiz';
import {
  loadPlannerTopics,
  resetPlannerTopics,
  savePlannerTopics,
} from './topicPlannerStorage';
import {
  clearCurriculumPath,
  loadCurriculumPath,
} from './curriculumPathStorage';
import { isWorkshopCategory } from './sessionCategoryUtils';
import type { OptionalSessionPurpose } from '../config/liveSessionTypes';

export type CreateSessionProvider = 'google_meet' | 'livekit';

/** Create UI always uses LiveKit; google_meet kept for legacy drafts only. */
export const STREAMING_ENGINE_OPTIONS: ReadonlyArray<{
  value: CreateSessionProvider;
  label: string;
}> = [{ value: 'livekit', label: 'LiveKit (in-app)' }];

export interface CreateSessionDraft {
  sessionCategory: SessionCategory;
  trackId: string;
  courseId: string;
  /** Used to parse month/year for auto session name (e.g. 7M 2026). */
  courseName?: string;
  batchId: string;
  batchLabel: string;
  /** Optional sessions may target multiple batches. */
  batchIds?: string[];
  studentCount: number;
  /** Display names selected in the UI. */
  subjects: string[];
  /** Matching subject_id values used in session titles + create payload. */
  subjectIds?: string[];
  selectedTopics: string[];
  topicCovered: string;
  workshopDays: number;
  dayTopics: string[][];
  /** Per-day start date/time for online workshops. */
  daySchedules?: WorkshopDayTiming[];
  deliveryTrainerId: string;
  /** @deprecated Removed from create UI; kept optional for legacy drafts. */
  localTrainerIds?: string[];
  /** @deprecated Removed from create UI; kept optional for legacy drafts. */
  coordinatorIds?: string[];
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  durationMinutes: number;
  trainerId: string;
  trainerName: string;
  trainerAvatarUrl: string;
  sessionType: 'group' | 'personal';
  /** Always livekit for new creates from Step 1. */
  sessionProvider: CreateSessionProvider;
  /** Legacy Google Meet drafts only. */
  meetLink?: string;
  autoSessionName: string;
  /** LiveKit-only join media defaults + waiting room. */
  waitingRoomEnabled?: boolean;
  trainerDefaultMic?: boolean;
  trainerDefaultCamera?: boolean;
  studentDefaultMic?: boolean;
  studentDefaultCamera?: boolean;
  /** When false, provision LiveKit SFU with record=false (default true). */
  recordingEnabled?: boolean;
  /** Optional / support session purpose (Step 1). */
  optionalPurpose?: OptionalSessionPurpose;
  /** Q&A pause window minutes (Step 4). */
  qaDurationMinutes?: number;
  /**
   * Off by default. When the trainer turns this on, a practice test is
   * assigned after the session ends.
   */
  practiceTestEnabled?: boolean;
  /**
   * When true, topics/subtopics come from course_live_session_plans.
   * Trainer may only set subtopic durations + session date/time (no add/remove).
   */
  fromCoursePlan?: boolean;
  /** Stable key from the planned draft (e.g. lcm:sql:001). */
  planSessionKey?: string;
  /** Course-plan template title before period (e.g. LCM SQL - 001). */
  planSessionTitle?: string;
  /** Mongo course_live_session_plans.plan_id when seeded from plan. */
  courseLiveSessionPlanId?: string;
}

const DRAFT_KEY = 'codelive-create-session-draft';
const FLOW_KEY = 'codelive-admin-create-session-flow';
/**
 * Set before clearing draft on success so draft-missing guards don't bounce to Step 1.
 * - listing: redirect to View Sessions
 * - suppress: stay silent (caller navigates elsewhere, e.g. add-students)
 */
const EXIT_KEY = 'codelive-create-exit';

export type CreateFlowExitMode = 'listing' | 'suppress';

export function isAdminCreateSessionFlow(): boolean {
  return sessionStorage.getItem(FLOW_KEY) === '1';
}

/** Step 1: mark wizard as active before navigating to Prepare. */
export function startAdminCreateSessionFlow(): void {
  sessionStorage.removeItem(EXIT_KEY);
  sessionStorage.setItem(FLOW_KEY, '1');
}

/** Mark intentional leave after create success (before clearing the draft). */
export function markCreateFlowExit(mode: CreateFlowExitMode = 'listing'): void {
  sessionStorage.setItem(EXIT_KEY, mode);
}

export function peekCreateFlowExit(): CreateFlowExitMode | null {
  const v = sessionStorage.getItem(EXIT_KEY);
  if (v === 'listing' || v === 'suppress') return v;
  return null;
}

export function clearCreateFlowExit(): void {
  sessionStorage.removeItem(EXIT_KEY);
}

/** Step 4 (success) or abort: drop draft + flow flag + side stores. */
export function clearAdminCreateSessionFlow(): void {
  sessionStorage.removeItem(FLOW_KEY);
  sessionStorage.removeItem(DRAFT_KEY);
  clearCurriculumPath();
  resetPlannerTopics();
}

/** Step 1: persist form values for later steps. */
export function saveCreateSessionDraft(draft: CreateSessionDraft): void {
  sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
}

/** Merge-patch the in-progress draft (no-op if missing). */
export function patchCreateSessionDraft(
  patch: Partial<CreateSessionDraft>,
): CreateSessionDraft | null {
  const prev = loadCreateSessionDraft();
  if (!prev) return null;
  const next = { ...prev, ...patch };
  saveCreateSessionDraft(next);
  return next;
}

export function loadCreateSessionDraft(): CreateSessionDraft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CreateSessionDraft;
  } catch {
    return null;
  }
}

/** True when Steps 2–4 may render (flow started + draft present). */
export function hasValidCreateDraft(): boolean {
  return isAdminCreateSessionFlow() && loadCreateSessionDraft() !== null;
}

function dayTopicsKey(days: string[][] | undefined): string {
  return JSON.stringify(
    (days || []).map((day) =>
      (day || []).map((t) => String(t || '').trim()).filter(Boolean),
    ),
  );
}

/**
 * Whether Step 1 Continue should reseed planner/curriculum.
 * Live Class topics are chosen on Prepare — never wipe that on Create Continue.
 * Workshop reseeds only when day topic assignment changes (or stores empty).
 */
export function shouldReseedPlannerFromCreate(
  prev: CreateSessionDraft | null,
  next: Pick<
    CreateSessionDraft,
    | 'sessionCategory'
    | 'workshopDays'
    | 'dayTopics'
    | 'selectedTopics'
    | 'fromCoursePlan'
    | 'planSessionKey'
  >,
): boolean {
  const plannerEmpty =
    loadPlannerTopics().length === 0 && loadCurriculumPath().length === 0;
  if (plannerEmpty) return true;

  // Plan-backed create: reseed only when the chosen planned session changes.
  if (next.fromCoursePlan) {
    if (!prev?.fromCoursePlan) return true;
    return String(prev.planSessionKey || '') !== String(next.planSessionKey || '');
  }
  // Left plan-backed flow → clear and reseed free-form.
  if (prev?.fromCoursePlan) return true;

  const nextWorkshop = isWorkshopCategory(next.sessionCategory);
  if (!nextWorkshop) {
    // Live Class / optional: Prepare owns the topic path.
    return false;
  }

  if (!prev || !isWorkshopCategory(prev.sessionCategory)) return true;
  if (Number(prev.workshopDays || 1) !== Number(next.workshopDays || 1)) {
    return true;
  }
  return dayTopicsKey(prev.dayTopics) !== dayTopicsKey(next.dayTopics);
}

/**
 * Step 1 → Step 3 prep: seed the topic planner from selected topic names only.
 */
export function seedPlannerTopicsFromDraft(
  topicNames: string[],
  options?: { workshop?: boolean; dayTopics?: string[][] },
): void {
  if (options?.workshop && options.dayTopics?.length) {
    seedWorkshopPlannerTopics(options.dayTopics);
    return;
  }
  const cleaned = topicNames.map((t) => t.trim()).filter(Boolean);
  const topics: TimelineTopicItem[] = cleaned.map((title, index) => ({
    id: `draft-topic-${index + 1}`,
    index: index + 1,
    title,
    durationMinutes: options?.workshop
      ? getWorkshopTopicDurationMinutes(title)
      : 15,
    description: options?.workshop
      ? 'Workshop topic selected during session creation.'
      : 'Selected during Prepare Session.',
  }));
  // Fresh create — drop stale Prepare path so Step 2 picks up seeded topics
  clearCurriculumPath();
  savePlannerTopics(topics);
}

/** Seed planner topics grouped by workshop day. */
export function seedWorkshopPlannerTopics(dayTopics: string[][]): void {
  const topics: TimelineTopicItem[] = [];
  let index = 0;
  dayTopics.forEach((day, dayIndex) => {
    for (const raw of day) {
      const title = String(raw || '').trim();
      if (!title) continue;
      index += 1;
      topics.push({
        id: `draft-topic-${index}`,
        index,
        title,
        durationMinutes: getWorkshopTopicDurationMinutes(title),
        description: `Workshop Day ${dayIndex + 1}`,
        workshopDayIndex: dayIndex,
      });
    }
  });
  clearCurriculumPath();
  savePlannerTopics(topics);
}
